/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo } from 'react'
import { Layout } from 'antd'
import { useLocation } from 'react-router-dom'
import SidebarMenu from './SidebarMenu'
import useWindowSize from 'src/hooks/useWindowSize'

const { Sider } = Layout

type CollapseType = 'clickTrigger' | 'responsive'

interface SidebarProps {
  collapsed: boolean
  onCollapse: (collapsed: boolean, type: CollapseType) => void
}

/**
 * Sidebar component with collapsible navigation menu
 * Integrates with Ant Design Layout.Sider
 */
const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const location = useLocation()
  const isPaymentPage = location?.pathname?.includes('pay')
  const isRegistrationInvitePage =
    location?.pathname?.includes('/registration/')
  const isLoginPage = location?.pathname?.includes('/login')
  const isRegisterPage = location?.pathname?.includes('/signup')
  const isForgotPasswordPage = location?.pathname?.includes('/forgot-password')
  const isVerifyEmailPage = location?.pathname?.includes('/verify-email')
  const isPasswordResetPage = location?.pathname?.includes('/password-reset')
  const isTestPage = location?.pathname?.includes('/ant-test')
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])

  useEffect(() => {
    if (isMobile) {
      onCollapse(true, 'clickTrigger')
    }
  }, [isMobile])

  if (
    isPaymentPage ||
    isLoginPage ||
    isRegistrationInvitePage ||
    isRegisterPage ||
    isForgotPasswordPage ||
    isVerifyEmailPage ||
    isPasswordResetPage ||
    isTestPage
  ) {
    return null
  }

  return (
    <Sider
      collapsible
      collapsed={isMobile ? true : collapsed}
      onCollapse={onCollapse}
      trigger={null}
      width={284}
      collapsedWidth={56}
      breakpoint='md'
      style={{
        overflowX: 'hidden',
        overflowY: 'auto',
        position: 'fixed',
        left: 0,
        top: 48, // Header height
        bottom: 0,
        zIndex: 10,
        paddingTop: 16,
        transition: 'width 0.2s ease-in-out',
        backgroundColor: '#212935',
      }}
      theme='dark'
    >
      <SidebarMenu
        collapsed={collapsed}
        currentPath={location.pathname}
        onExpand={() => onCollapse(false, 'clickTrigger')}
        isMobile={isMobile}
      />
    </Sider>
  )
}

export default Sidebar
